/**
 * Property Editor Registry Package
 *
 * Complete API for managing property editors in the Atlas Forms Designer
 *
 * Main exports:
 * 1. PropertyEditorRegistry - Singleton registry for editors
 * 2. initializeEditors() - Load all editors at startup
 * 3. usePropertyEditors() - React hook for editor access
 * 4. DefaultEditor - Fallback editor for unknown types
 *
 * SOLID Architecture:
 * - Single Responsibility: Registry only manages editor lifecycle and lookup
 * - Open/Closed: Extensible via plugins and loader patterns
 * - Liskov Substitution: All editors implement PropertyEditor interface
 * - Interface Segregation: Focused, minimal interfaces
 * - Dependency Inversion: Components depend on interfaces, not implementations
 */

// ─── Core Registry & Types ───────────────────────────────────────────────

import { PropertyEditorRegistry } from './PropertyEditorRegistry';
import type { PropertyEditor } from './PropertyEditor.types';
import { DefaultEditor } from './DefaultEditor';

export { PropertyEditorRegistry };
export type { PropertyEditorRegistry as IPropertyEditorRegistry } from './PropertyEditorRegistry';

export type {
  PropertyEditor,
  PropertyEditorProps,
  PropertyEditorPlugin,
  ControlConfig,
  TypedFormControl,
} from './PropertyEditor.types';

export { PropertyEditorError, isPropertyEditor } from './PropertyEditor.types';

// ─── Editor Loading Infrastructure ───────────────────────────────────────

export { initializeEditors, getInitializationResult, isInitialized, resetEditorLoader } from './EditorLoader';

export type { EditorLoaderConfig, EditorInitializationResult } from './EditorLoader';

// ─── React Hooks ─────────────────────────────────────────────────────────

export { usePropertyEditors, usePropertyEditorsContext } from './usePropertyEditors';

export type { UsePropertyEditorsReturn } from './usePropertyEditors';

// ─── Default/Fallback Editor ─────────────────────────────────────────────

export {
  DefaultEditor,
  getDefaultEditor,
  isDefaultEditor,
  createDefaultEditorForType,
} from './DefaultEditor';

// ─── Convenience Exports ──────────────────────────────────────────────────

/**
 * Get or create default editor for unknown type
 *
 * @param type - Control type
 * @returns PropertyEditor for the type
 */
export function getEditorOrDefault(type: string): PropertyEditor {
  const editor = PropertyEditorRegistry.get(type);
  if (editor) return editor;
  return PropertyEditorRegistry.get('__default__') || DefaultEditor;
}

/**
 * Register multiple editors at once
 *
 * @param editors - Array of editors to register
 * @throws PropertyEditorError if any editor is invalid
 */
export function registerEditors(editors: PropertyEditor[]): void {
  for (const editor of editors) {
    PropertyEditorRegistry.register(editor);
  }
}

/**
 * Get all editors of a specific category
 *
 * @param category - Category name (e.g., 'styling', 'container', 'chart')
 * @returns Array of editors in that category
 */
export function getEditorsByCategory(category: string): PropertyEditor[] {
  return PropertyEditorRegistry.getAll().filter((editor) => {
    const editorType = editor.type;
    // Simple heuristic: check if editor type contains category
    return editorType.includes(category) || editor.label.toLowerCase().includes(category);
  });
}

// ─── Auto-initialization Module ───────────────────────────────────────────

/**
 * Auto-initialize editors on module import
 * Only runs in browser environment
 * Safe to call multiple times (idempotent)
 *
 * Wrapped in async to defer until after all module imports complete
 */
(async () => {
  if (typeof window !== 'undefined' && typeof document !== 'undefined') {
    // Import dynamically to ensure they're available at runtime
    try {
      const { isInitialized: checkInitialized, initializeEditors: initEditors } = await import(
        './EditorLoader'
      );

      // Use next microtask to ensure everything is ready
      if (typeof queueMicrotask !== 'undefined') {
        queueMicrotask(() => {
          if (!checkInitialized()) {
            initEditors({ verbose: false }).catch((error: any) => {
              console.error('[PropertyEditorRegistry] Auto-initialization failed:', error);
            });
          }
        });
      } else {
        Promise.resolve().then(() => {
          if (!checkInitialized()) {
            initEditors({ verbose: false }).catch((error: any) => {
              console.error('[PropertyEditorRegistry] Auto-initialization failed:', error);
            });
          }
        });
      }
    } catch (error) {
      console.error('[PropertyEditorRegistry] Failed to auto-initialize editors:', error);
    }
  }
})();

export default PropertyEditorRegistry;

// ─── Phase 5: Additional Registries ──────────────────────────────────────

// Validation Rule Registry
export {
  ValidationRuleRegistry,
  type ValidationRuleRegistry as IValidationRuleRegistry,
} from './ValidationRuleRegistry';

export type {
  ValidationRule,
  ValidationRuleConfig,
  ValidationContext,
  ValidationResult,
  ValidationRulePlugin,
} from './ValidationRule.types';

export { ValidationRuleError, isValidationRule } from './ValidationRule.types';

export {
  RequiredRule,
  EmailRule,
  MinLengthRule,
  MaxLengthRule,
  RangeRule,
  PatternRule,
  UrlRule,
  BuiltInValidationRules,
  registerBuiltInValidationRules,
} from './BuiltInValidationRules';

// Action Registry
export { ActionRegistry, type ActionRegistry as IActionRegistry } from './ActionRegistry';

export type {
  FormAction,
  ActionConfig,
  ActionContext,
  ActionResult,
  ActionPlugin,
} from './Action.types';

export { ActionError, isFormAction } from './Action.types';

export {
  SubmitAction,
  ResetAction,
  NavigateAction,
  AlertAction,
  ApiCallAction,
  SetValueAction,
  BuiltInActions,
  registerBuiltInActions,
} from './BuiltInActions';
